import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import Row from 'react-bootstrap/lib/Row';
import Col from 'react-bootstrap/lib/Col';
import Label from 'react-bootstrap/lib/Label';
import Button from 'react-bootstrap/lib/Button';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';

// Components
import InboxItem from './InboxItem';
import Loader from '../Loader';
import history from '../../core/history';

// Locale
import messages from '../../locale/messages';

// Styles
import s from './Inbox.css';
import bt from '../../components/commonStyle.css';

const UnreadMessages = ({ userId, loading, getUnreadThreads }) => {
  const intl = useIntl();

  const renderLabel = (status, noStyle) => {
    let label, style;
    switch (status) {
      case 'inquiry':
        label = <FormattedMessage {...messages.messageStatus1} />;
        style = 'info';
        break;
      case 'preApproved':
        label = <FormattedMessage {...messages.messageStatus2} />;
        style = 'primary';
        break;
      case 'declined':
        label = <FormattedMessage {...messages.messageStatus3} />;
        style = 'danger';
        break;
      case 'approved':
        label = <FormattedMessage {...messages.messageStatus4} />;
        style = 'success';
        break;
      case 'pending':
        label = <FormattedMessage {...messages.messageStatus5} />;
        style = 'warning';
        break;
      case 'cancelledByHost':
        label = <FormattedMessage {...messages.messageStatus6} />;
        style = 'danger';
        break;
      case 'cancelledByGuest':
        label = <FormattedMessage {...messages.messageStatus7} />;
        style = 'danger';
        break;
      case 'intantBooking':
      case 'instantBooking':
      case 'confirmed':
        label = <FormattedMessage {...messages.messageStatus8} />;
        style = 'success';
        break;
      case 'expired':
        label = <FormattedMessage {...messages.messageStatus9} />;
        style = 'danger';
        break;
      case 'requestToBook':
        label = <FormattedMessage {...messages.messageStatus10} />;
        style = 'primary';
        break;
      case 'completed':
        label = <FormattedMessage {...messages.inboxCompleted} />;
        style = 'success';
        break;
      default:
        return null;
    }

    return noStyle ? label : <Label bsStyle={style}>{label}</Label>;
  };

  if (loading) {
    return <Loader type="text" />;
  }

  if (getUnreadThreads?.length > 0) {
    return (
      <div className={cx(s.progressContainerResponsive)}>
        <Row>
          <Col xs={12}>
            <ul className={cx(s.listLayout, 'listLayoutArbic')}>
              {getUnreadThreads.map((item, index) => {
                const type = userId === item.host ? 'host' : 'guest';

                if (item.threadItemUnread && item.guestProfile && item.hostProfile) {
                  const profile =
                    type === 'host' ? item.guestProfile : item.hostProfile;

                  return (
                    <InboxItem
                      key={index}
                      threadId={item.id}
                      type={type}
                      profileId={profile.profileId}
                      picture={profile.picture}
                      displayName={profile.firstName}
                      content={
                        item.threadItemUnread.content ??
                        renderLabel(item.threadItemUnread.type, true)
                      }
                      createdAt={item.threadItemUnread.createdAt}
                      city={item.listData.city}
                      state={item.listData.state}
                      country={item.listData.country}
                      startDate={item.threadItemUnread.startDate}
                      endDate={item.threadItemUnread.endDate}
                      status={renderLabel(item.threadItemUnread.type)}
                      sentBy={item.threadItemUnread.sentBy}
                      read={item.threadItemUnread.isRead}
                    />
                  );
                } else {
                  return <li key={index} />;
                }
              })}
            </ul>

            {getUnreadThreads.length > 4 && (
              <div className={cx(s.loadMore)}>
                <Button
                  onClick={() => history.push('/inbox')}
                  className={cx(s.btn, bt.btnPrimary, s.loadMoreBtn)}
                >
                  <FormattedMessage {...messages.loadMore} />...
                </Button>
              </div>
            )}
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className={s.emptyMessagesContainer}>
      <div className={s.emptyMessagesIconWrapper}>
        <svg className={s.emptyMessagesRays} width="60" height="20" viewBox="0 0 60 20">
          <line x1="42" y1="14" x2="48" y2="4" stroke="#009cff" strokeWidth="2" strokeLinecap="round" />
          <line x1="50" y1="18" x2="57" y2="12" stroke="#009cff" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <div className={s.emptyMessagesCircle}>
          <svg width="28" height="24" viewBox="0 0 24 20" fill="none" stroke="#009cff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="20" height="16" rx="2"></rect>
            <path d="m22 4-10 7L2 4"></path>
          </svg>
        </div>
      </div>
      <h4 className={s.emptyMessagesTitle}>No tienes mensajes nuevos</h4>
      <p className={s.emptyMessagesSubtitle}>Aquí aparecerán tus conversaciones con los anfitriones.</p>
    </div>
  );
};

UnreadMessages.propTypes = {
  userId: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  getUnreadThreads: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    listData: PropTypes.shape({
      city: PropTypes.string.isRequired,
      state: PropTypes.string.isRequired,
      country: PropTypes.string.isRequired,
    }),
    guestProfile: PropTypes.shape({
      profileId: PropTypes.number.isRequired,
      picture: PropTypes.string,
      displayName: PropTypes.string.isRequired,
    }),
    hostProfile: PropTypes.shape({
      profileId: PropTypes.number.isRequired,
      picture: PropTypes.string,
      displayName: PropTypes.string.isRequired,
    }),
    threadItemUnread: PropTypes.shape({
      type: PropTypes.string.isRequired,
      content: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      isRead: PropTypes.bool.isRequired,
      sentBy: PropTypes.string.isRequired,
      createdAt: PropTypes.string.isRequired,
    }),
  })),
};

UnreadMessages.defaultProps = {
  loading: true,
  getUnreadThreads: [],
};

export default withStyles(s)(UnreadMessages);
